import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { handleArmSite } from "./SiteAboutPageUtils/handleArmSite";
import { useNavigate } from "react-router-dom";
import { AlertState, SiteInfo } from "./SiteAboutPageUtils/siteAboutInterfaces";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import { FormHelperText } from "@mui/material";
import { handleArmSiteFlexSchedules } from "./SiteAboutPageUtils/handleArmSiteFlexSchedules";
import { handleCreateSelectedSchedulesArrays } from "./SiteAboutPageUtils/handleCreateSelectedSchedulesArrays";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import {
  FlexWindow,
  flexScheduleWindow,
} from "./SiteAboutPageUtils/flexScheduleWindow";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export const ArmSiteFlexScheduleDialog = ({
  armSiteFlexScheduleDialogOpen,
  setArmSiteFlexScheduleDialogOpen,
  setSiteInfo,
  setBackdropLoadingScreenOn,
  setAlertState,
  setSyncNeeded,
  setArmedStatus,
  setArmButtonDisabled,
  // siteSchedules,
  setToolTipTitle,
  // "reboot" when the picker was opened from a reboot-to-apply flow
  // (RebootDialog handoff) — logs "Manual Reboot" instead of "Manual Arm"
  // (ENG-1404).
  endpointAction = "arm",
  // The site's timezone, for telling which schedules are inside their
  // window right now (ENG-1968).
  siteTimezone,
}: {
  armSiteFlexScheduleDialogOpen: boolean;
  setArmSiteFlexScheduleDialogOpen: Dispatcher<boolean>;
  setSiteInfo: Dispatcher<SiteInfo>;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  setAlertState: Dispatcher<AlertState>;
  setSyncNeeded: Dispatcher<boolean>;
  setArmedStatus: Dispatcher<boolean>;
  setArmButtonDisabled: any;
  // siteSchedules: any;
  setToolTipTitle: any;
  endpointAction?: "arm" | "reboot";
  siteTimezone?: string;
}) => {
  const { siteSchedules } = useSiteAboutStore();

  const navigate = useNavigate();

  // A manual arm or reboot sticks until the schedule's next scheduled
  // disarm, so restarting a schedule outside its window arms its products
  // right away, e.g. a 22:00–06:00 schedule restarted at 10:00 stays armed
  // until 06:00 tomorrow (SUP-669). Show each window, and when applying
  // settings, pre-select only the schedules that are running now.
  const windows: FlexWindow[] = [0, 1, 2].map((index) =>
    flexScheduleWindow(siteSchedules[index]?.schedule, siteTimezone)
  );

  const [selectedValue, setSelectedValue] = React.useState(() => {
    const preselect = (index: number) =>
      endpointAction === "reboot" && windows[index].inWindow === true;
    return { one: preselect(0), two: preselect(1), three: preselect(2) };
  });

  const productNames = (index: number) =>
    (siteSchedules[index]?.product ?? [])
      .map((product: number) => `${productIds.get(product)}`)
      .join(", ");

  const checkedFlags = [
    selectedValue.one,
    selectedValue.two,
    selectedValue.three,
  ];

  // Starting a schedule outside its window while another schedule that
  // shares a product is running runs that product twice, so every alert
  // arrives twice (ENG-1970). Only the running schedules left unticked count:
  // ticked ones are covered by the duplicate-products warning below.
  const runningDuplicates = (index: number) => {
    const mine: number[] = siteSchedules[index]?.product ?? [];
    return [0, 1, 2].flatMap((other) => {
      if (other === index || checkedFlags[other]) return [];
      if (windows[other].inWindow !== true || !siteSchedules[other]) return [];
      const shared = (siteSchedules[other].product ?? []).filter(
        (product: number) => mine.includes(product)
      );
      return shared.length
        ? [
            {
              name: siteSchedules[other].display_name as string,
              products: shared
                .map((product: number) => `${productIds.get(product)}`)
                .join(", "),
            },
          ]
        : [];
    });
  };

  const scheduleLabel = (index: number, checked: boolean) => {
    const window = windows[index];
    const outside = window.inWindow === false;
    return (
      <div>
        <div>
          {`${
            siteSchedules[index]?.display_name
          } (Active Products: ${productNames(index)})`}
        </div>
        <div
          className="text-sm text-gray-600"
          data-testid={`flex-window-${index}`}
        >
          {window.label}
          {window.inWindow === true && " · running now"}
          {outside && " · outside its window"}
        </div>
        {checked && outside && (
          <FormHelperText error data-testid={`flex-window-warning-${index}`}>
            {`${
              endpointAction === "reboot" ? "Restarting" : "Arming"
            } it now arms ${productNames(
              index
            )} right away. It stays armed until ${
              window.endsAt
                ? `its next scheduled disarm at ${window.endsAt}`
                : "its next scheduled disarm"
            }.`}
          </FormHelperText>
        )}
        {checked &&
          outside &&
          runningDuplicates(index).map((duplicate) => (
            <FormHelperText
              error
              key={duplicate.name}
              data-testid={`flex-running-duplicate-${index}`}
            >
              {`${duplicate.name} is running ${duplicate.products} now. ${
                endpointAction === "reboot" ? "Restarting" : "Arming"
              } ${siteSchedules[index]?.display_name} would run ${
                duplicate.products
              } twice and duplicate alerts.`}
            </FormHelperText>
          ))}
      </div>
    );
  };

  const [duplicateProducts, setDuplicateProducts] = React.useState(false);

  const products = JSON.parse(localStorage.products);
  const productIds = new Map();

  products.forEach((product: any) => {
    productIds.set(product.id, product.name);
  });

  const handleCheckForSameProducts = () => {
    const selectedProducts: number[] = [];

    if (selectedValue.one) {
      siteSchedules[0].product.forEach((product: number) => {
        selectedProducts.push(product);
      });
    }
    if (selectedValue.two) {
      siteSchedules[1].product.forEach((product: number) => {
        selectedProducts.push(product);
      });
    }
    if (selectedValue.three) {
      siteSchedules[2].product.forEach((product: number) => {
        selectedProducts.push(product);
      });
    }

    //check selectedProducts for duplicates
    const duplicates = selectedProducts.filter(
      (item, index) => selectedProducts.indexOf(item) != index
    );

    if (duplicates.length > 0) {
      setDuplicateProducts(true);
    } else {
      setDuplicateProducts(false);
    }
  };

  React.useEffect(() => {
    handleCheckForSameProducts();
  }, [selectedValue]);

  return (
    <Dialog
      open={armSiteFlexScheduleDialogOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar sx={{ background: "#001943" }}>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            {endpointAction === "reboot" ? "Restart Site" : "Arm Site"}
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {endpointAction === "reboot"
            ? "Flex scheduling is enabled. Select the schedules to restart so they pick up the new settings. Schedules that are running now are selected. Schedules outside their window pick up the new settings at their next scheduled arm, so they don't need a restart."
            : "Flex scheduling is enabled. Select the schedules you would like to arm. Each stays armed until its next scheduled disarm."}
        </DialogContentText>
        <FormGroup className="pt-[20px]">
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedValue.one}
                onChange={(event) =>
                  setSelectedValue({
                    ...selectedValue,
                    one: event.target.checked,
                  })
                }
              />
            }
            label={scheduleLabel(0, selectedValue.one)}
          />
          {siteSchedules[1] && siteSchedules[1]?.product?.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedValue.two}
                  onChange={(event) =>
                    setSelectedValue({
                      ...selectedValue,
                      two: event.target.checked,
                    })
                  }
                />
              }
              label={scheduleLabel(1, selectedValue.two)}
            />
          )}
          {siteSchedules[2] && siteSchedules[2].product.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedValue.three}
                  onChange={(event) =>
                    setSelectedValue({
                      ...selectedValue,
                      three: event.target.checked,
                    })
                  }
                />
              }
              label={scheduleLabel(2, selectedValue.three)}
            />
          )}
        </FormGroup>
        {duplicateProducts && (
          <FormHelperText error>
            Schedules have been selected that contain the same products. Please
            note that this could result in duplicate alerts.
          </FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <OrangeTextButton
          onClick={() => {
            setArmSiteFlexScheduleDialogOpen(false);
            setSiteInfo((previousState: SiteInfo) => {
              return { ...previousState, armed: false };
            });
          }}
          text="Cancel"
          disabled={false}
        />
        <OrangeTextButton
          onClick={() => {
            handleArmSiteFlexSchedules(
              setBackdropLoadingScreenOn,
              setSiteInfo,
              setAlertState,
              setSyncNeeded,
              setArmedStatus,
              navigate,
              setArmButtonDisabled,
              handleCreateSelectedSchedulesArrays(selectedValue, siteSchedules),
              setToolTipTitle,
              endpointAction
            );

            setSiteInfo((previousState: SiteInfo) => {
              return { ...previousState, armed: true };
            });

            setArmSiteFlexScheduleDialogOpen(false);
          }}
          text={endpointAction === "reboot" ? "Restart" : "Arm Site"}
          disabled={
            !selectedValue.one && !selectedValue.two && !selectedValue.three
          }
        />
      </DialogActions>
    </Dialog>
  );
};
